import React, { useState, useEffect } from 'react';
import { X, Save, Beaker, Eye, Edit2 } from 'lucide-react';
import { dataMappingAPI } from '../../services/api';
import toast from 'react-hot-toast';

interface MedicationViewEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  medication: any | null;
  mode: 'view' | 'edit';
}

const MedicationViewEditModal: React.FC<MedicationViewEditModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  medication,
  mode: initialMode,
}) => {
  const [mode, setMode] = useState<'view' | 'edit'>(initialMode);
  const [formData, setFormData] = useState({
    med_id: '',
    data_sources_id: '',
    investigational_code: '',
    chemical_name: '',
    international_nonproprietary_name: '',
    generic_name: '',
    abbreviation1: '',
    abbreviation2: '',
    internal_name1: '',
    internal_name2: '',
    internal_name3: '',
    internal_name4: '',
    internal_name5: '',
    internal_name6: '',
    internal_name7: '',
    internal_name8: '',
    internal_name9: '',
    internal_name10: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Load medication data when modal opens
  useEffect(() => {
    if (isOpen && medication) {
      // Check if it's from mapping table or basic table
      const isMappingFormat = medication.Med_ID || medication.med_id;
      
      if (isMappingFormat) {
        // Mapping format
        setFormData({
          med_id: medication.Med_ID || medication.med_id || '',
          data_sources_id: medication.Data_Sources_ID || medication.data_sources_id || '',
          investigational_code: medication.Investigational_Code || medication.investigational_code || '',
          chemical_name: medication.Chemical_Name || medication.chemical_name || '',
          international_nonproprietary_name: medication.International_Nonproprietary_Name || medication.international_nonproprietary_name || '',
          generic_name: medication.Generic_Name || medication.generic_name || '',
          abbreviation1: medication.Abbreviation1 || medication.abbreviation1 || '',
          abbreviation2: medication.Abbreviation2 || medication.abbreviation2 || '',
          internal_name1: medication.Internal_Name1 || medication.internal_name1 || '',
          internal_name2: medication.Internal_Name2 || medication.internal_name2 || '',
          internal_name3: medication.Internal_Name3 || medication.internal_name3 || '',
          internal_name4: medication.Internal_Name4 || medication.internal_name4 || '',
          internal_name5: medication.Internal_Name5 || medication.internal_name5 || '',
          internal_name6: medication.Internal_Name6 || medication.internal_name6 || '',
          internal_name7: medication.Internal_Name7 || medication.internal_name7 || '',
          internal_name8: medication.Internal_Name8 || medication.internal_name8 || '',
          internal_name9: medication.Internal_Name9 || medication.internal_name9 || '',
          internal_name10: medication.Internal_Name10 || medication.internal_name10 || '',
        });
      } else {
        // Basic format (from medications table)
        const medId = medication.id ? `M${String(medication.id).padStart(4, '0')}` : '';
        setFormData({
          med_id: medId,
          data_sources_id: '',
          investigational_code: '',
          chemical_name: '',
          international_nonproprietary_name: '',
          generic_name: medication.name || '',
          abbreviation1: '',
          abbreviation2: '',
          internal_name1: '',
          internal_name2: '',
          internal_name3: '',
          internal_name4: '',
          internal_name5: '',
          internal_name6: '',
          internal_name7: '',
          internal_name8: '',
          internal_name9: '',
          internal_name10: '',
        });
      }
      setMode(initialMode);
    }
  }, [isOpen, medication, initialMode]);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrors({});

    try {
      await dataMappingAPI.createMedicationMapping(formData);
      toast.success('Medication updated successfully');
      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('Error updating medication:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to update medication';
      toast.error(errorMessage);
      
      if (error.response?.data?.errors) {
        setErrors(error.response.data.errors);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen || !medication) return null;

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
          <div className="flex items-center gap-3">
            <Beaker className="h-6 w-6 text-[var(--accent)]" />
            <h2 className="text-xl font-semibold text-gray-900">
              {mode === 'view' ? 'View Medication' : 'Edit Medication'}
            </h2>
          </div>
          <div className="flex items-center gap-2">
            {mode === 'view' && (
              <button
                onClick={() => setMode('edit')}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                title="Edit"
              >
                <Edit2 className="h-5 w-5 text-gray-600" />
              </button>
            )}
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <X className="h-5 w-5 text-gray-500" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {mode === 'view' ? (
            // View Mode
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Med_ID</label>
                  <p className="text-sm text-gray-900 font-mono">{formData.med_id || '-'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Data_Sources_ID</label>
                  <p className="text-sm text-gray-900 font-mono">{formData.data_sources_id || '-'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Generic Name</label>
                  <p className="text-sm text-gray-900">{formData.generic_name || '-'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">INN</label>
                  <p className="text-sm text-gray-900">{formData.international_nonproprietary_name || '-'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Abbreviation 1</label>
                  <p className="text-sm text-gray-900">{formData.abbreviation1 || '-'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Abbreviation 2</label>
                  <p className="text-sm text-gray-900">{formData.abbreviation2 || '-'}</p>
                </div>
                {formData.chemical_name && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Chemical Name</label>
                    <p className="text-sm text-gray-900">{formData.chemical_name}</p>
                  </div>
                )}
                {formData.investigational_code && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Investigational Code</label>
                    <p className="text-sm text-gray-900">{formData.investigational_code}</p>
                  </div>
                )}
              </div>
              
              {(formData.internal_name1 || formData.internal_name2 || formData.internal_name3) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Internal Names</label>
                  <div className="space-y-1">
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(num => {
                      const value = formData[`internal_name${num}` as keyof typeof formData] as string;
                      return value ? (
                        <p key={num} className="text-sm text-gray-900">
                          <span className="font-medium">Internal Name {num}:</span> {value}
                        </p>
                      ) : null;
                    })}
                  </div>
                </div>
              )}
            </div>
          ) : (
            // Edit Mode
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Med_ID and Data_Sources_ID */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="med_id" className="block text-sm font-medium text-gray-700 mb-2">
                    <Beaker className="inline h-4 w-4 mr-1" />
                    Med_ID
                  </label>
                  <input
                    type="text"
                    id="med_id"
                    value={formData.med_id}
                    onChange={(e) => handleInputChange('med_id', e.target.value.toUpperCase())}
                    placeholder="M0001"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--accent)] focus:border-[var(--accent)]"
                  />
                </div>
                <div>
                  <label htmlFor="data_sources_id" className="block text-sm font-medium text-gray-700 mb-2">
                    Data_Sources_ID
                  </label>
                  <input
                    type="text"
                    id="data_sources_id"
                    value={formData.data_sources_id}
                    onChange={(e) => handleInputChange('data_sources_id', e.target.value)}
                    placeholder="D001"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--accent)] focus:border-[var(--accent)]"
                  />
                </div>
              </div>

              {/* Generic Name and INN */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="generic_name" className="block text-sm font-medium text-gray-700 mb-2">
                    Generic Name
                  </label>
                  <input
                    type="text"
                    id="generic_name"
                    value={formData.generic_name}
                    onChange={(e) => handleInputChange('generic_name', e.target.value)}
                    placeholder="Rifampin"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--accent)] focus:border-[var(--accent)]"
                  />
                </div>
                <div>
                  <label htmlFor="international_nonproprietary_name" className="block text-sm font-medium text-gray-700 mb-2">
                    International Nonproprietary Name (INN)
                  </label>
                  <input
                    type="text"
                    id="international_nonproprietary_name"
                    value={formData.international_nonproprietary_name}
                    onChange={(e) => handleInputChange('international_nonproprietary_name', e.target.value)}
                    placeholder="Rifampicin"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--accent)] focus:border-[var(--accent)]"
                  />
                </div>
              </div>

              {/* Abbreviations */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="abbreviation1" className="block text-sm font-medium text-gray-700 mb-2">
                    Abbreviation 1
                  </label>
                  <input
                    type="text"
                    id="abbreviation1"
                    value={formData.abbreviation1}
                    onChange={(e) => handleInputChange('abbreviation1', e.target.value.toUpperCase())}
                    placeholder="RIF"
                    maxLength={10}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--accent)] focus:border-[var(--accent)]"
                  />
                </div>
                <div>
                  <label htmlFor="abbreviation2" className="block text-sm font-medium text-gray-700 mb-2">
                    Abbreviation 2
                  </label>
                  <input
                    type="text"
                    id="abbreviation2"
                    value={formData.abbreviation2}
                    onChange={(e) => handleInputChange('abbreviation2', e.target.value.toUpperCase())}
                    placeholder="R"
                    maxLength={10}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--accent)] focus:border-[var(--accent)]"
                  />
                </div>
              </div>

              {/* Internal Names (collapsible) */}
              <details className="border border-gray-200 rounded-lg p-4">
                <summary className="cursor-pointer text-sm font-medium text-gray-700 mb-4">
                  Internal Names (Optional)
                </summary>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
                    <div key={num}>
                      <label htmlFor={`internal_name${num}`} className="block text-sm font-medium text-gray-700 mb-2">
                        Internal Name {num}
                      </label>
                      <input
                        type="text"
                        id={`internal_name${num}`}
                        value={formData[`internal_name${num}` as keyof typeof formData] as string}
                        onChange={(e) => handleInputChange(`internal_name${num}`, e.target.value)}
                        placeholder={`Internal name ${num}`}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--accent)] focus:border-[var(--accent)]"
                      />
                    </div>
                  ))}
                </div>
              </details>

              {/* Submit buttons */}
              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => {
                    if (initialMode === 'view') {
                      setMode('view');
                    } else {
                      onClose();
                    }
                  }}
                  className="px-4 py-2 rounded-lg border border-gray-300 text-sm text-gray-700 hover:bg-gray-50"
                >
                  {initialMode === 'view' ? 'Cancel' : 'Close'}
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-4 py-2 rounded-lg bg-[var(--accent)] text-white text-sm font-medium hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default MedicationViewEditModal;
